import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  Bar,
  Legend,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { Select } from "../../../components/Select";
import { KPI } from "../../../components/primitives.jsx";
import { localizedName } from "../../../lib/localizedName";
import { useChartPalette } from "../../../theme/useChartPalette";
import { formatDate, intlLocale } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import { controlPointApi } from "../../../api/controlPoints";
import LabInsightsTab from "./LabInsightsTab";
import { missingPointLabel } from "./archivePointLabel";
import {
  ParameterMovementCard,
  ParameterMovementChart,
  VISINA_GRAFIKONA,
  VISINA_SA_BROJEVIMA,
} from "./ParameterMovementCard";
import { ArticleCombobox } from "../../../components/ArticleCombobox";
import {
  labArchiveApi,
  type AnalysisDetail,
  type AnalysisRow,
  type ArchiveArticleOption,
  type ArchiveOverview,
  type ControlPoint,
  type ControlPointKind,
  type MeasurementRow,
  type MonthTally,
  type ParameterMovement,
  type ParameterSummary,
  type ParameterUsage,
  type TallyFilter,
} from "../../../api/labArchive";

const PAGE = 50;

/**
 * The laboratory archive: everything Bankom's own lab measured between November 2016 and September 2026.
 *
 * Two readings of the same history, because the quality service asks two different questions of it. The
 * analyses tab answers „what was done on this sample on that day"; the parameter tab answers „how has
 * moisture moved over ten years, and where did it leave the limit". The second is the reason the archive
 * was brought over at all — in the old program a parameter could only be read one analysis at a time.
 *
 * Read only, and the page says so. A measurement from 2019 carries no author and no time in the source,
 * so there is nobody in whose name it could be corrected here. New work is entered through „Nalazi na
 * LOT-u", which records both.
 *
 * One rule runs through every value shown: where a result could not be judged — no limit for the
 * article, or a limit whose unit is under review — it is marked as not judged, never as within limits.
 */
export default function LabArchivePage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.resolvedLanguage ?? "sr";

  // Na ovaj ekran se ulazi i sa nalaza na LOT-u, gde panel kaže koliko analiza artikal ima a ne vodi
  // nikuda. Artikal zato dolazi adresom, i tada se otvara upravo kartica na kojoj se analize vide.
  const [params, setParams] = useSearchParams();
  const artikalIzAdrese = params.get("erpProductId") ?? "";
  const [tab, setTab] = useState<"insights" | "analyses" | "parameter">(
    artikalIzAdrese ? "analyses" : "insights",
  );
  const [overview, setOverview] = useState<ArchiveOverview | null>(null);
  const [points, setPoints] = useState<ControlPoint[]>([]);

  useEffect(() => {
    void labArchiveApi
      .overview()
      .then(setOverview)
      .catch((e: ApiError) => toast.error(e.message));
    void controlPointApi
      .list(false)
      .then(setPoints)
      .catch((e: ApiError) => toast.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pointName = useCallback(
    (p: ControlPoint) => localizedName({ name: p.code, nameI18n: p.nameI18n }, lang),
    [lang],
  );

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("lab.title", "Arhiv laboratorije")}</h1>
          <div className="sub">
            {t(
              "lab.lead",
              "Nalazi iz Bankomovog laboratorijskog programa, od novembra 2016. Arhiv se čita, ne menja: merenje iz 2019. u izvoru nema ni autora ni vreme, pa nema u čije ime da se ispravi. Novo se unosi na „Nalazi na LOT-u”.",
            )}
          </div>
        </div>
        <div className="seg">
          <button type="button" aria-pressed={tab === "insights"} onClick={() => setTab("insights")}>
            {t("lab.tabInsights", "Pregled")}
          </button>
          <button type="button" aria-pressed={tab === "analyses"} onClick={() => setTab("analyses")}>
            {t("lab.tabAnalyses", "Analize")}
          </button>
          <button type="button" aria-pressed={tab === "parameter"} onClick={() => setTab("parameter")}>
            {t("lab.tabParameter", "Po parametru")}
          </button>
        </div>
      </div>

      {!overview ? (
        <PanelLoading text={t("common.loading")} />
      ) : (
        <>
          <div className="kpi-grid" style={{ marginBottom: 0 }}>
            <KPI label={t("lab.kpi.analyses", "Analiza")} value={overview.analyses.toLocaleString(lang)} delta={undefined} trend={undefined} icon={undefined} />
            <KPI
              label={t("lab.kpi.measurements", "Merenja")}
              value={overview.measurements.toLocaleString(lang)}
            delta={undefined} trend={undefined} icon={undefined} />
            <KPI
              label={t("lab.kpi.neverMeasured", "Naručeno, nemereno")}
              value={overview.orderedNeverMeasured.toLocaleString(lang)}
            delta={undefined} trend={undefined} icon={undefined} />
            {/* The archive ends on the day of its last analysis, not at the end of a year, so the
                card names that day: read as a bare year, the last one would look complete. The
                date is kept in one piece, so a narrow card breaks the line after the dash. */}
            <KPI
              label={t("lab.kpi.span", "Opseg")}
              value={
                overview.firstAnalysis && overview.lastAnalysis
                  ? `${overview.firstAnalysis.slice(0, 4)}–${formatDate(overview.lastAnalysis, lang).replace(/ /g, "\u00a0")}`
                  : "—"
              }
              delta={undefined}
              trend={undefined}
              icon={undefined}
            />
          </div>

          <ArchiveCaveats overview={overview} />

          {tab === "insights" ? (
            <LabInsightsTab />
          ) : tab === "analyses" ? (
            <AnalysesTab
              points={points}
              pointName={pointName}
              overview={overview}
              article={artikalIzAdrese}
              articleName={params.get("naziv") ?? ""}
              onPickArticle={(sifra, naziv) => {
                // Izbor artikla ide kroz adresu, kao i dolazak sa šarže, pa je jedno stanje i jedno
                // mesto istine — a odabrani artikal preživi osvežavanje i može da se pošalje vezom.
                const sledeci = new URLSearchParams(params);
                if (sifra) {
                  sledeci.set("erpProductId", sifra);
                  if (naziv) sledeci.set("naziv", naziv);
                  else sledeci.delete("naziv");
                } else {
                  sledeci.delete("erpProductId");
                  sledeci.delete("naziv");
                }
                setParams(sledeci, { replace: true });
              }}
            />
          ) : (
            <ParameterTab points={points} pointName={pointName} overview={overview} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * What the archive cannot answer, said once and up front.
 *
 * These numbers are the honest edge of the data and belong next to the totals, not in a footnote: an
 * analysis without a control point cannot be grouped by where the sample was taken, a lot label that
 * matches more than one batch cannot be followed forward, and a limit whose unit is in doubt judges
 * nothing. A reader who does not know this reads every chart as complete.
 */
function ArchiveCaveats({ overview }: { overview: ArchiveOverview }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "sr";
  const n = (v: number) => v.toLocaleString(lang);

  const stavke: string[] = [];
  if (overview.testsChosenIndividually > 0) {
    stavke.push(
      t("lab.caveat.individual", "{{n}} analiza nema kontrolnu tačku jer su testovi birani pojedinačno.", {
        n: n(overview.testsChosenIndividually),
      }),
    );
  }
  // Without a point and not picked one by one: either there was no request, so no group, or the group
  // is one the lab program later deleted from its codebook. Two different gaps, said apart.
  const obrisanaGrupa = overview.groupNotInCodebook;
  const bezZahteva = overview.withoutControlPoint - overview.testsChosenIndividually - obrisanaGrupa;
  if (bezZahteva > 0) {
    stavke.push(
      t("lab.caveat.noGroup", "{{n}} analiza nema zahtev za uzorkovanje, pa ni grupu uzorkovanja.", {
        n: n(bezZahteva),
      }),
    );
  }
  if (obrisanaGrupa > 0) {
    stavke.push(
      t(
        "lab.caveat.groupNotInCodebook",
        "{{n}} analiza ima grupu uzorkovanja koje više nema u šifarniku laboratorije, pa ni kontrolnu tačku.",
        { n: n(obrisanaGrupa) },
      ),
    );
  }
  if (overview.lotLabelWithoutBatch > 0) {
    stavke.push(
      t(
        "lab.caveat.lot",
        "{{n}} analiza nosi oznaku lota koja se ne vezuje za tačno jednu šaržu, pa je ostala nevezana.",
        { n: n(overview.lotLabelWithoutBatch) },
      ),
    );
  }
  if (overview.limitsUnderReview > 0) {
    stavke.push(
      t(
        "lab.caveat.limits",
        "{{n}} granice su pod proverom zbog jedinice i ne ocenjuju rezultat.",
        { n: n(overview.limitsUnderReview) },
      ),
    );
  }
  if (stavke.length === 0) return null;

  return (
    <div className="card">
      <div className="card-h">
        <strong>{t("lab.caveat.title", "Šta arhiv ne može da odgovori")}</strong>
      </div>
      <div className="card-b">
        <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
          {stavke.map((s) => (
            <li key={s} className="muted">
              {s}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** How a result sits against the limit that applied to it. Not judged is its own answer, not a pass. */
function LimitTag({ outside }: { outside: boolean | null }) {
  const { t } = useTranslation();
  if (outside === null) {
    return <span className="tag neutral">{t("lab.notJudged", "nije ocenjeno")}</span>;
  }
  return outside ? (
    <span className="tag err">{t("lab.outside", "van granice")}</span>
  ) : (
    <span className="tag ok">{t("lab.inside", "u granici")}</span>
  );
}

function kindLabel(kind: ControlPointKind | null, t: (k: string, d: string) => string): string {
  switch (kind) {
    case "RAW":
      return t("lab.kind.RAW", "Sirovina");
    case "SEMI_PRODUCT":
      return t("lab.kind.SEMI_PRODUCT", "Poluproizvod");
    case "PRODUCT":
      return t("lab.kind.PRODUCT", "Proizvod");
    case "UTILITY":
      return t("lab.kind.UTILITY", "Voda i pomoćno");
    default:
      return "—";
  }
}

/** A month key like 2025-08 rendered the way the axis reads it: month and year, in the reader's language. */
function mesecLabel(month: string, lang: string): string {
  const d = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return month;
  return d.toLocaleDateString(intlLocale(lang), { month: "short", year: "2-digit" });
}

/**
 * A month the response does not carry is drawn as a zero, not skipped.
 *
 * The monthly tally returns only the months that actually hold an analysis. An axis that lists those one
 * after another puts June 2021 next to March 2023 and reads as an unbroken run — and the gap is exactly
 * what this chart is asked for: a month with no analysis under these conditions is a finding, not a hole
 * in the data.
 */
export function poMesecima(months: MonthTally[]): MonthTally[] {
  if (months.length === 0) return [];
  const sortirani = [...months].sort((a, b) => a.month.localeCompare(b.month));
  const prvi = sortirani[0].month.split("-").map(Number);
  if (!Number.isFinite(prvi[0]) || !Number.isFinite(prvi[1])) return sortirani;
  const po = new Map(sortirani.map((m) => [m.month, m]));
  const zadnji = sortirani[sortirani.length - 1].month;
  const izlaz: MonthTally[] = [];
  let godina = prvi[0];
  let mesec = prvi[1];
  // The ceiling is a guard, not a span: fifty years is more than any archive holds, and a malformed last
  // month would otherwise spin this loop without end.
  for (let i = 0; i < 600; i++) {
    const kljuc = `${godina}-${String(mesec).padStart(2, "0")}`;
    izlaz.push(po.get(kljuc) ?? { month: kljuc, analyses: 0, measurements: 0 });
    if (kljuc === zadnji) break;
    mesec += 1;
    if (mesec > 12) {
      mesec = 1;
      godina += 1;
    }
  }
  return izlaz;
}

/* ------------------------------------------------------------------ analize */

/**
 * The analyses list: one row per analysis, narrowed by any combination of filters.
 *
 * The filters are all optional on purpose. The quality manager arrives wanting the whole thing and narrows
 * from there — which is also why the row count is always shown, so narrowing is visible as it happens.
 */
function AnalysesTab({
  points,
  pointName,
  overview,
  article,
  articleName,
  onPickArticle,
}: {
  points: ControlPoint[];
  pointName: (p: ControlPoint) => string;
  overview: ArchiveOverview;
  article: string;
  articleName: string;
  onPickArticle: (erpProductId: string, name: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.resolvedLanguage ?? "sr";

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [pointId, setPointId] = useState("");
  const [kind, setKind] = useState("");
  const [parameterId, setParameterId] = useState("");
  const [lot, setLot] = useState("");
  // Artikal dolazi iz adrese, jer se na ovaj ekran ulazi sa nalaza na LOT-u: tamo panel kaže koliko
  // analiza artikal ima, a odavde se one gledaju. Filter se vidi kao oznaka koja se može skinuti.
  const [erpProductId, setErpProductId] = useState(article);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);

  const [rows, setRows] = useState<AnalysisRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState<AnalysisDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // The whole article list at once: it is small, it does not change between two questions, and a filter
  // that asks the server per keystroke would answer the same thing every time. A failed read leaves the
  // list empty, and the picker then simply offers nothing — the rest of the screen keeps working.
  const [articles, setArticles] = useState<ArchiveArticleOption[]>([]);

  useEffect(() => {
    void labArchiveApi
      .articles()
      .then(setArticles)
      .catch(() => setArticles([]));
  }, []);

  // One filter for the list and for the chart above it. Two places that assemble the same condition each
  // on their own drift apart at the first edit, and a chart that plots something other than the table
  // beneath it is an error nobody sees — both views look correct.
  const filter: TallyFilter = useMemo(
    () => ({
      from: from || undefined,
      to: to || undefined,
      controlPointId: pointId || undefined,
      kind: (kind || undefined) as ControlPointKind | undefined,
      parameterId: parameterId || undefined,
      erpProductId: erpProductId || undefined,
      lot: lot.trim() || undefined,
      q: q.trim() || undefined,
    }),
    [from, to, pointId, kind, parameterId, erpProductId, lot, q],
  );

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await labArchiveApi.analyses({ ...filter, page, size: PAGE });
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.error((e as ApiError).message);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page]);

  useEffect(() => {
    setErpProductId(article);
    setPage(0);
  }, [article]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openRow(row: AnalysisRow) {
    if (open?.head.id === row.id) {
      setOpen(null);
      return;
    }
    setLoadingDetail(true);
    try {
      setOpen(await labArchiveApi.analysis(row.id));
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setLoadingDetail(false);
    }
  }

  function clearFilters() {
    setFrom("");
    setTo("");
    setPointId("");
    setKind("");
    setParameterId("");
    setLot("");
    setQ("");
    setPage(0);
  }

  const pointOptions = useMemo(
    () => [
      { value: "", label: t("lab.filter.allPoints", "Sve kontrolne tačke") },
      ...points.map((p) => ({ value: p.id, label: `${pointName(p)} (${p.analyses.toLocaleString(lang)})` })),
    ],
    [points, pointName, lang, t],
  );

  const parameterOptions = useMemo(
    () => [
      { value: "", label: t("lab.filter.allParameters", "Svi parametri") },
      ...overview.parameters.map((p) => ({
        value: p.parameterId,
        label: `${localizedName({ name: p.key, nameI18n: p.nameI18n }, lang)} (${p.measurements.toLocaleString(lang)})`,
      })),
    ],
    [overview.parameters, lang, t],
  );

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="card">
      <div className="card-h" style={{ flexWrap: "wrap", gap: 8 }}>
        <strong>{t("lab.analyses.title", "Analize")}</strong>
        <span className="muted">
          {t("lab.analyses.count", "{{n}} analiza", { n: total.toLocaleString(lang) })}
        </span>
      </div>
      <div className="card-b">
        {erpProductId && (
          <div className="toolbar" style={{ marginBottom: 10 }}>
            <span className="tag neutral">
              {articleName
                ? t("lab.filter.articleNamed", "Artikal: {{naziv}}", { naziv: articleName })
                : t("lab.filter.articleCode", "Artikal: šifra {{sifra}}", { sifra: erpProductId })}
            </span>
            <button type="button" className="btn ghost sm" onClick={() => onPickArticle("", "")}>
              {t("lab.filter.clearArticle", "Prikaži sve artikle")}
            </button>
          </div>
        )}
        <div className="toolbar">
          <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {t("lab.filter.article", "Artikal")}
            <ArticleCombobox
              articles={articles}
              value={erpProductId}
              fallbackName={articleName}
              onChange={onPickArticle}
            />
          </label>
          <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {t("lab.filter.from", "Od")}
            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(0);
              }}
              style={{ width: 150 }}
            />
          </label>
          <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {t("lab.filter.to", "Do")}
            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(0);
              }}
              style={{ width: 150 }}
            />
          </label>
          <Select
            value={pointId}
            options={pointOptions}
            onChange={(v) => {
              setPointId(v);
              setPage(0);
            }}
            ariaLabel={t("lab.filter.point", "Kontrolna tačka")}
            style={{ minWidth: 220 }}
          />
          <Select
            value={kind}
            options={[
              { value: "", label: t("lab.filter.allKinds", "Sve vrste") },
              { value: "RAW", label: kindLabel("RAW", t) },
              { value: "SEMI_PRODUCT", label: kindLabel("SEMI_PRODUCT", t) },
              { value: "PRODUCT", label: kindLabel("PRODUCT", t) },
              { value: "UTILITY", label: kindLabel("UTILITY", t) },
            ]}
            onChange={(v) => {
              setKind(v);
              setPage(0);
            }}
            ariaLabel={t("lab.filter.kind", "Vrsta")}
            style={{ minWidth: 160 }}
          />
          <Select
            value={parameterId}
            options={parameterOptions}
            onChange={(v) => {
              setParameterId(v);
              setPage(0);
            }}
            ariaLabel={t("lab.filter.parameter", "Parametar")}
            style={{ minWidth: 220 }}
          />
          <input
            className="input"
            placeholder={t("lab.filter.lot", "LOT")}
            value={lot}
            onChange={(e) => {
              setLot(e.target.value);
              setPage(0);
            }}
            style={{ width: 150 }}
          />
          <input
            className="input"
            placeholder={t("lab.filter.q", "Artikal, napomena, uzorkivač")}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            style={{ minWidth: 220, flex: 1 }}
          />
          <button type="button" className="btn ghost" onClick={clearFilters}>
            {t("lab.filter.clear", "Očisti")}
          </button>
        </div>

        <AnalysesChartCard filter={filter} overview={overview} lang={lang} />

        {rows === null ? (
          <PanelLoading text={t("common.loading")} />
        ) : rows.length === 0 ? (
          <div className="empty">{t("lab.analyses.none", "Nema analize po ovim uslovima.")}</div>
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("lab.col.date", "Datum")}</th>
                  <th>{t("lab.col.point", "Kontrolna tačka")}</th>
                  <th>{t("lab.col.product", "Artikal")}</th>
                  <th>{t("lab.col.lot", "LOT")}</th>
                  <th>{t("lab.col.shift", "Smena")}</th>
                  <th>{t("lab.col.sampler", "Uzorkivač")}</th>
                  <th style={{ textAlign: "right" }}>{t("lab.col.tests", "Testova")}</th>
                  <th style={{ textAlign: "right" }}>{t("lab.col.measurements", "Merenja")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr
                      onClick={() => void openRow(r)}
                      style={{ cursor: "pointer" }}
                      aria-expanded={open?.head.id === r.id}
                    >
                      <td style={{ whiteSpace: "nowrap" }}>{formatDate(r.analysedOn, lang)}</td>
                      <td>
                        {r.controlPointId ? (
                          <>
                            {localizedName({ name: r.controlPointCode ?? "", nameI18n: r.controlPointName }, lang)}{" "}
                            <span className="tag neutral">{kindLabel(r.controlPointKind, t)}</span>
                          </>
                        ) : (
                          <span className="muted">{missingPointLabel(t, r) ?? "—"}</span>
                        )}
                      </td>
                      <td>{r.productName ?? <span className="muted">—</span>}</td>
                      <td>
                        {r.lotText ? (
                          <span style={{ fontFamily: "var(--mono)" }}>{r.lotText}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>{r.shiftCode ?? <span className="muted">—</span>}</td>
                      <td>{r.samplerName ?? <span className="muted">—</span>}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.tests}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {r.measurements}
                      </td>
                    </tr>
                    {open?.head.id === r.id && (
                      <tr>
                        <td colSpan={8} style={{ background: "var(--surface-2)" }}>
                          {loadingDetail ? <PanelLoading text={t("common.loading")} /> : <AnalysisDetailView detail={open} />}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>

            <div className="toolbar" style={{ marginTop: 14, marginBottom: 0, justifyContent: "flex-end" }}>
              <span className="muted">
                {t("lab.page", "Strana {{p}} od {{n}}", { p: page + 1, n: pages })}
              </span>
              <button
                type="button"
                className="btn ghost"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t("lab.prev", "Prethodna")}
              </button>
              <button
                type="button"
                className="btn ghost"
                disabled={page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("lab.next", "Sledeća")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A chart for exactly what the list is showing right now.
 *
 * A column of numbers says neither where the value stood nor on which days anything was measured, and
 * those are the two questions the archive gets opened for. So above the list stands a picture of the very
 * condition the list was narrowed by.
 *
 * Two drawings, because two different questions make sense. With a parameter chosen, the movement of that
 * one quantity is drawn. Without one, no value can go on an axis — moisture in percent and a bacteria
 * count are not the same quantity — so what gets drawn is what can be counted regardless of parameter:
 * how many analyses there are per month.
 *
 * The span is what had to be settled here. Movement with no dates answers for its own most recent year,
 * and a parameter's most recent year need not carry a single day for the chosen article: protein was
 * measured in the archive in 2026 too, while article Tesla Protein TP 420 has not one day in 2026 — the
 * chart would stand empty beside a list of 141 rows. So when no dates are entered, the call is sent the
 * span of the whole archive, the same one the list is showing.
 *
 * Three of the list's conditions the movement call cannot take: kind, LOT and the search term. They are
 * not passed over in silence but named — a chart that does not follow the filter and fails to say so lies
 * more convincingly than an empty chart.
 */
function AnalysesChartCard({
  filter,
  overview,
  lang,
}: {
  filter: TallyFilter;
  overview: ArchiveOverview;
  lang: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const palette = useChartPalette();

  const { from, to, controlPointId, kind, parameterId, erpProductId, lot, q } = filter;
  const [movement, setMovement] = useState<ParameterMovement | null>(null);
  const [months, setMonths] = useState<MonthTally[] | null>(null);
  const [pao, setPao] = useState(false);

  // A list with no dates means the whole archive, so the chart has to cover the whole archive as well.
  const od = from ?? overview.firstAnalysis ?? undefined;
  const doDatuma = to ?? overview.lastAnalysis ?? undefined;

  useEffect(() => {
    let otkazano = false;
    setMovement(null);
    setMonths(null);
    setPao(false);
    const posao = parameterId
      ? labArchiveApi
          .movement(parameterId, { from: od, to: doDatuma, controlPointId, erpProductId })
          .then((r) => {
            if (!otkazano) setMovement(r);
          })
      : labArchiveApi.tally({ from, to, controlPointId, kind, erpProductId, lot, q }).then((r) => {
          if (!otkazano) setMonths(r);
        });
    void posao.catch((e: ApiError) => {
      if (otkazano) return;
      setPao(true);
      toast.error(e.message);
    });
    return () => {
      otkazano = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterId, from, to, od, doDatuma, controlPointId, kind, erpProductId, lot, q]);

  const stubici = useMemo(
    () => poMesecima(months ?? []).map((m) => ({ ...m, labela: mesecLabel(m.month, lang) })),
    [months, lang],
  );

  const nepraceni = parameterId
    ? [
        kind ? t("lab.filter.kind", "Vrsta") : null,
        lot ? t("lab.filter.lot", "LOT") : null,
        q ? t("lab.chart.search", "pretragu") : null,
      ].filter((x): x is string => x !== null)
    : [];

  // The wait holds the height of what is coming, so the table below does not jump when the data lands.
  const visina = parameterId ? VISINA_SA_BROJEVIMA : VISINA_GRAFIKONA + 80;
  const cekanje = !pao && (parameterId ? movement === null : months === null);

  return (
    <div className="card flat" style={{ margin: "0 0 16px" }}>
      <div className="card-h" style={{ flexWrap: "wrap", gap: 8 }}>
        <strong>
          {parameterId
            ? t("lab.chart.movementTitle", "Kretanje izabranog parametra")
            : t("lab.chart.tallyTitle", "Analize po mesecu")}
        </strong>
        {movement && (
          <span className="muted">
            {localizedName({ name: movement.key, nameI18n: movement.nameI18n }, lang)} · {movement.unit}
          </span>
        )}
      </div>
      <div className="card-b">
        {nepraceni.length > 0 && (
          <p className="muted" style={{ margin: "0 0 12px", fontSize: 12 }}>
            {t(
              "lab.chart.notApplied",
              "Grafikon ne prati {{spisak}} — ti uslovi sužavaju samo spisak ispod.",
              { spisak: nepraceni.join(", ") },
            )}
          </p>
        )}

        {cekanje ? (
          <div
            style={{
              minHeight: visina,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PanelLoading text={t("common.loading")} />
          </div>
        ) : pao ? (
          <div className="empty">
            {t("lab.chart.failed", "Grafikon nije mogao da se pročita. Spisak ispod stoji.")}
          </div>
        ) : parameterId && movement ? (
          <ParameterMovementChart
            data={movement}
            lang={lang}
            range={od && doDatuma ? { from: od, to: doDatuma } : null}
            emptyText={t(
              "lab.chart.movementNone",
              "Po ovim uslovima parametar nije izmeren ni jednom, pa kretanje nema ni jednu tačku.",
            )}
          />
        ) : stubici.length === 0 ? (
          <div className="empty">
            {t("lab.chart.tallyNone", "Po ovim uslovima nema ni jedne analize, pa grafikon nema šta da prikaže.")}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={VISINA_GRAFIKONA}>
              <ComposedChart data={stubici} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="labela"
                  tick={{ fontSize: 11, fill: palette.ink }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  yAxisId="a"
                  tick={{ fontSize: 11, fill: palette.ink }}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                />
                <YAxis yAxisId="m" orientation="right" hide />
                <Legend
                  verticalAlign="top"
                  align="right"
                  height={24}
                  wrapperStyle={{ fontSize: 12, color: "var(--ink-3)" }}
                />
                <Tooltip
                  contentStyle={{
                    background: palette.surface,
                    border: `1px solid ${palette.border}`,
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: palette.inkStrong }}
                  formatter={(value, name) => [Number(value).toLocaleString(lang), String(name)]}
                />
                <Bar
                  yAxisId="a"
                  dataKey="analyses"
                  name={t("lab.chart.analyses", "Analiza")}
                  fill={palette.chart[0] || palette.brandInk}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="m"
                  type="monotone"
                  dataKey="measurements"
                  name={t("lab.chart.measurements", "Merenja")}
                  stroke={palette.ok}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <p className="muted" style={{ margin: "12px 0 0", maxWidth: "76ch", lineHeight: 1.55 }}>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One analysis opened up: every test ordered on it, with everything measured under each.
 *
 * The test name from the lab program is shown beside ours. A technician who worked that program for ten
 * years recognises „Enterobactericae" faster than the corrected spelling, and the archive is read by
 * exactly those people.
 */
function AnalysisDetailView({ detail }: { detail: AnalysisDetail }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "sr";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "12px 0" }}>
      <div className="muted" style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        <span>{t("lab.detail.legacyId", "Broj u programu: {{n}}", { n: detail.head.legacyId })}</span>
        {detail.requestedAt && (
          <span>
            {t("lab.detail.requestedAt", "Zahtev otvoren: {{d}}", {
              d: formatDate(detail.requestedAt, lang),
            })}
          </span>
        )}
        {detail.head.pallet && (
          <span>{t("lab.detail.pallet", "Paleta: {{p}}", { p: detail.head.pallet })}</span>
        )}
        {detail.head.note && <span>{t("lab.detail.note", "Napomena: {{n}}", { n: detail.head.note })}</span>}
      </div>

      {detail.items.length === 0 ? (
        <div className="empty">
          {t("lab.detail.noItems", "Na ovoj analizi nije naručen ni jedan test.")}
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("lab.detail.test", "Test")}</th>
              <th>{t("lab.detail.sample", "Uzorak")}</th>
              <th style={{ textAlign: "right" }}>{t("lab.detail.value", "Vrednost")}</th>
              <th>{t("lab.detail.limit", "Granica")}</th>
              <th>{t("lab.detail.verdict", "Ocena")}</th>
            </tr>
          </thead>
          <tbody>
            {detail.items.flatMap((i) => {
              const ime = localizedName({ name: i.parameterKey, nameI18n: i.parameterName }, lang);
              const drugacijeIme = i.legacyTestName && i.legacyTestName !== ime ? i.legacyTestName : null;
              const granica = i.limit
                ? i.limit.valueTo !== null
                  ? `${i.limit.valueFrom} – ${i.limit.valueTo} ${i.unit}`
                  : `≥ ${i.limit.valueFrom} ${i.unit}`
                : null;

              if (i.results.length === 0) {
                return [
                  <tr key={i.id}>
                    <td>
                      {ime}
                      {drugacijeIme && <span className="muted"> · {drugacijeIme}</span>}
                    </td>
                    <td colSpan={4} className="muted">
                      {t("lab.detail.ordered", "Naručeno, nije izmereno.")}
                    </td>
                  </tr>,
                ];
              }

              return i.results.map((r, idx) => (
                <tr key={r.id}>
                  <td>
                    {idx === 0 ? (
                      <>
                        {ime}
                        {drugacijeIme && <span className="muted"> · {drugacijeIme}</span>}
                      </>
                    ) : (
                      <span className="muted">↳</span>
                    )}
                  </td>
                  <td>{r.sampleLabel ?? <span className="muted">—</span>}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {r.codedValue
                      ? r.codedValue
                      : r.valueNum !== null
                        ? `${r.valueNum} ${i.unit}`.trim()
                        : (r.valueText ?? "—")}
                  </td>
                  <td className="muted">
                    {granica ?? "—"}
                    {i.limit?.needsReview && (
                      <>
                        {" "}
                        <span className="tag warn">{t("lab.limitUnderReview", "jedinica u proveri")}</span>
                      </>
                    )}
                  </td>
                  <td>
                    <LimitTag outside={r.outsideLimit} />
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Jedan parametar kroz deset godina.
 *
 * Tabela ispod drži iste brojeve, ali raspon od najnižeg do najvišeg se u koloni ne vidi. Senka je taj
 * raspon, linija je prosek — pa se odmah vidi i kad se vrednost pomerila, i kad se samo rasula.
 */
function ParameterTrend({ summary, lang }: { summary: ParameterSummary; lang: string }) {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const podaci = summary.perYear.map((y) => ({
    year: y.year,
    avg: Number(y.avgValue),
    opseg: [Number(y.minValue), Number(y.maxValue)] as [number, number],
    min: Number(y.minValue),
    max: Number(y.maxValue),
  }));

  return (
    <div style={{ marginBottom: 16 }}>
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={podaci} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="year" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} width={58} />
          <Tooltip
            contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
            formatter={(value, name) =>
              Array.isArray(value)
                ? [`${value[0]} – ${value[1]} ${summary.unit}`, String(name)]
                : [`${Number(value).toLocaleString(lang)} ${summary.unit}`, String(name)]
            }
          />
          <Area
            dataKey="opseg"
            name={t("lab.param.range", "Od najnižeg do najvišeg")}
            stroke="none"
            fill={palette.chart[0] || palette.brand}
            fillOpacity={0.14}
          />
          <Line
            type="monotone"
            dataKey="avg"
            name={t("lab.param.avg", "Prosek")}
            stroke={palette.chart[0] || palette.brandInk}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {t(
          "lab.param.trendNote",
          "Senka je raspon od najniže do najviše izmerene vrednosti te godine, linija je prosek. Gde se senka naglo širi a prosek ne menja, pomerila se tačnost unosa a ne proces.",
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ po parametru */

/**
 * One parameter across the whole archive — the reading the old program could not give at all.
 *
 * There a result was reachable only through the analysis it sat on, so a question like „how has urease
 * moved since 2018" meant opening fourteen thousand analyses. Here the parameter is the subject: the
 * yearly shape first, then the limits that applied, then every measurement.
 */
function ParameterTab({
  points,
  pointName,
  overview,
}: {
  points: ControlPoint[];
  pointName: (p: ControlPoint) => string;
  overview: ArchiveOverview;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.resolvedLanguage ?? "sr";

  const [selected, setSelected] = useState<string>(overview.parameters[0]?.parameterId ?? "");
  const [summary, setSummary] = useState<ParameterSummary | null>(null);
  const [rows, setRows] = useState<MeasurementRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pointId, setPointId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // The article is the movement card's filter, yet it is held here: the parameter list on the left also has
  // to know one is chosen, because its counts are for the whole archive and then no longer match the chart.
  const [articles, setArticles] = useState<ArchiveArticleOption[]>([]);
  const [articleId, setArticleId] = useState("");

  useEffect(() => {
    void labArchiveApi
      .articles()
      .then(setArticles)
      .catch(() => setArticles([]));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setSummary(null);
    void labArchiveApi
      .parameterSummary(selected)
      .then(setSummary)
      .catch((e: ApiError) => toast.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const load = useCallback(async () => {
    if (!selected) return;
    setRows(null);
    try {
      const res = await labArchiveApi.measurements(selected, {
        from: from || undefined,
        to: to || undefined,
        controlPointId: pointId || undefined,
        page,
        size: PAGE,
      });
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.error((e as ApiError).message);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, from, to, pointId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Grouped by discipline, because microbiology comes back days after chemistry and reads differently. */
  const groups = useMemo(() => {
    const by = new Map<string, ParameterUsage[]>();
    for (const p of overview.parameters) {
      const k = p.discipline ?? "CHEMISTRY";
      if (!by.has(k)) by.set(k, []);
      by.get(k)!.push(p);
    }
    const order = ["CHEMISTRY", "GRAIN", "MICROBIOLOGY"];
    return order
      .filter((k) => by.has(k))
      .map((k) => ({ discipline: k, items: by.get(k)! }));
  }, [overview.parameters]);

  const pages = Math.max(1, Math.ceil(total / PAGE));

  function disciplineLabel(d: string): string {
    if (d === "MICROBIOLOGY") return t("lab.discipline.MICROBIOLOGY", "Mikrobiologija");
    if (d === "GRAIN") return t("lab.discipline.GRAIN", "Sojino zrno");
    return t("lab.discipline.CHEMISTRY", "Hemija");
  }

  const izabraniArtikal = articles.find((a) => a.erpProductId === articleId);

  return (
    <div className="catalog-layout">
      <aside className="cat-side">
        <p className="muted" style={{ margin: "0 0 12px", fontSize: 12, lineHeight: 1.5 }}>
          {articleId
            ? t(
                "lab.param.sideScopeArticle",
                "Broj pored parametra je broj merenja u celom arhivu. Kartica „Kretanje kroz godinu” je sužena na {{naziv}}, pa su njeni brojevi manji od ovih.",
                { naziv: izabraniArtikal?.name || articleId },
              )
            : t("lab.param.sideScope", "Broj pored parametra je broj merenja u celom arhivu.")}
        </p>
        {groups.map((g) => (
          <div className="group" key={g.discipline}>
            <h3>{disciplineLabel(g.discipline)}</h3>
            <div className="cat-list">
              {g.items.map((p) => (
                <button
                  key={p.parameterId}
                  type="button"
                  className={p.parameterId === selected ? "active" : undefined}
                  onClick={() => {
                    setSelected(p.parameterId);
                    setPage(0);
                  }}
                >
                  <span>{localizedName({ name: p.key, nameI18n: p.nameI18n }, lang)}</span>
                  <span className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {p.measurements.toLocaleString(lang)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </aside>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        {!summary ? (
          <PanelLoading text={t("common.loading")} />
        ) : (
          <>
            <ParameterMovementCard
              parameterId={summary.parameterId}
              points={points}
              pointName={pointName}
              lang={lang}
              articles={articles}
              articleId={articleId}
              onArticle={(sifra) => setArticleId(sifra)}
            />

            <div className="card">
              <div className="card-h" style={{ flexWrap: "wrap", gap: 8 }}>
                <strong>{localizedName({ name: summary.key, nameI18n: summary.nameI18n }, lang)}</strong>
                <span className="muted">{summary.unit}</span>
                <span className="tag neutral">{disciplineLabel(summary.discipline)}</span>
                {/* Four labels that also stand on the card above. Which of them is narrowed has to be
                    said, or two readings of the same word disagree on one screen. */}
                <span className="muted" style={{ fontSize: 12 }}>
                  {t("lab.param.summaryScope", "Brojevi ispod su za ceo arhiv, bez filtera iz kartice iznad.")}
                </span>
              </div>
              <div className="card-b">
                <div className="kpi-grid" style={{ marginBottom: summary.perYear.length > 0 ? 16 : 0 }}>
                  <KPI
                    label={t("lab.param.measurements", "Merenja sa brojem")}
                    value={summary.measurements.toLocaleString(lang)}
                  delta={undefined} trend={undefined} icon={undefined} />
                  <KPI
                    label={t("lab.param.min", "Najniže")}
                    value={summary.minValue === null ? "—" : String(summary.minValue)}
                  delta={undefined} trend={undefined} icon={undefined} />
                  <KPI
                    label={t("lab.param.avg", "Prosek")}
                    value={summary.avgValue === null ? "—" : String(summary.avgValue)}
                  delta={undefined} trend={undefined} icon={undefined} />
                  <KPI
                    label={t("lab.param.max", "Najviše")}
                    value={summary.maxValue === null ? "—" : String(summary.maxValue)}
                  delta={undefined} trend={undefined} icon={undefined} />
                </div>

                {summary.orderedNeverMeasured > 0 && (
                  <p className="muted" style={{ margin: "0 0 12px" }}>
                    {t(
                      "lab.param.neverMeasured",
                      "{{n}} puta je ovaj test naručen a nije izmeren.",
                      { n: summary.orderedNeverMeasured.toLocaleString(lang) },
                    )}
                  </p>
                )}

                {summary.perYear.length > 0 && (
                  <>
                    <ParameterTrend summary={summary} lang={lang} />
                    <table className="tbl">
                    <thead>
                      <tr>
                        <th>{t("lab.param.year", "Godina")}</th>
                        <th style={{ textAlign: "right" }}>{t("lab.param.count", "Merenja")}</th>
                        <th style={{ textAlign: "right" }}>{t("lab.param.min", "Najniže")}</th>
                        <th style={{ textAlign: "right" }}>{t("lab.param.avg", "Prosek")}</th>
                        <th style={{ textAlign: "right" }}>{t("lab.param.max", "Najviše")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.perYear.map((y) => (
                        <tr key={y.year}>
                          <td>{y.year}</td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {y.measurements.toLocaleString(lang)}
                          </td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {y.minValue}
                          </td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {y.avgValue}
                          </td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {y.maxValue}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>

            {summary.limits.length > 0 && (
              <div className="card">
                <div className="card-h">
                  <strong>{t("lab.param.limits", "Granice iz laboratorijskog programa")}</strong>
                  <span className="muted">
                    {t(
                      "lab.param.limitsLead",
                      "Vise na artiklu, ne na specifikaciji proizvoda. Zato artikal bez granice nije mogao ni da se izmeri.",
                    )}
                  </span>
                </div>
                <div className="card-b">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>{t("lab.col.product", "Artikal")}</th>
                        <th style={{ textAlign: "right" }}>{t("lab.param.limitFrom", "Od")}</th>
                        <th style={{ textAlign: "right" }}>{t("lab.param.limitTo", "Do")}</th>
                        <th aria-label={t("lab.detail.verdict", "Ocena")} />
                      </tr>
                    </thead>
                    <tbody>
                      {summary.limits.map((l, idx) => (
                        <tr key={`${l.erpProductId ?? "x"}-${idx}`}>
                          <td>{l.productName ?? <span className="muted">—</span>}</td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {l.valueFrom}
                          </td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {l.valueTo ?? <span className="muted">—</span>}
                          </td>
                          <td>
                            {l.needsReview && (
                              <span className="tag warn">{t("lab.limitUnderReview", "jedinica u proveri")}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-h" style={{ flexWrap: "wrap", gap: 8 }}>
                <strong>{t("lab.param.history", "Sva merenja")}</strong>
                <span className="muted">
                  {t("lab.analyses.count", "{{n}} analiza", { n: total.toLocaleString(lang) })}
                </span>
              </div>
              <div className="card-b">
                <div className="toolbar">
                  <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {t("lab.filter.from", "Od")}
                    <input
                      className="input"
                      type="date"
                      value={from}
                      onChange={(e) => {
                        setFrom(e.target.value);
                        setPage(0);
                      }}
                      style={{ width: 150 }}
                    />
                  </label>
                  <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {t("lab.filter.to", "Do")}
                    <input
                      className="input"
                      type="date"
                      value={to}
                      onChange={(e) => {
                        setTo(e.target.value);
                        setPage(0);
                      }}
                      style={{ width: 150 }}
                    />
                  </label>
                  <Select
                    value={pointId}
                    options={[
                      { value: "", label: t("lab.filter.allPoints", "Sve kontrolne tačke") },
                      ...points.map((p) => ({ value: p.id, label: pointName(p) })),
                    ]}
                    onChange={(v) => {
                      setPointId(v);
                      setPage(0);
                    }}
                    ariaLabel={t("lab.filter.point", "Kontrolna tačka")}
                    style={{ minWidth: 220 }}
                  />
                </div>

                {rows === null ? (
                  <PanelLoading text={t("common.loading")} />
                ) : rows.length === 0 ? (
                  <div className="empty">
                    {t("lab.param.noHistory", "Nema merenja ovog parametra po ovim uslovima.")}
                  </div>
                ) : (
                  <>
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>{t("lab.col.date", "Datum")}</th>
                          <th>{t("lab.col.point", "Kontrolna tačka")}</th>
                          <th>{t("lab.col.product", "Artikal")}</th>
                          <th>{t("lab.col.lot", "LOT")}</th>
                          <th>{t("lab.detail.sample", "Uzorak")}</th>
                          <th style={{ textAlign: "right" }}>{t("lab.detail.value", "Vrednost")}</th>
                          <th>{t("lab.detail.verdict", "Ocena")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.resultId}>
                            <td style={{ whiteSpace: "nowrap" }}>{formatDate(r.analysedOn, lang)}</td>
                            <td>
                              {r.controlPointCode ? (
                                localizedName({ name: r.controlPointCode, nameI18n: r.controlPointName }, lang)
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                            <td>{r.productName ?? <span className="muted">—</span>}</td>
                            <td>
                              {r.lotText ? (
                                <span style={{ fontFamily: "var(--mono)" }}>{r.lotText}</span>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                            <td>{r.sampleLabel ?? <span className="muted">—</span>}</td>
                            <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                              {r.codedValue
                                ? r.codedValue
                                : r.valueNum !== null
                                  ? r.valueNum
                                  : (r.valueText ?? "—")}
                            </td>
                            <td>
                              <LimitTag outside={r.outsideLimit} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div
                      className="toolbar"
                      style={{ marginTop: 14, marginBottom: 0, justifyContent: "flex-end" }}
                    >
                      <span className="muted">
                        {t("lab.page", "Strana {{p}} od {{n}}", { p: page + 1, n: pages })}
                      </span>
                      <button
                        type="button"
                        className="btn ghost"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                      >
                        {t("lab.prev", "Prethodna")}
                      </button>
                      <button
                        type="button"
                        className="btn ghost"
                        disabled={page + 1 >= pages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        {t("lab.next", "Sledeća")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
